import { AxiosError } from "axios";
import { useCallback, type ReactNode } from "react";
import { UseFormSetError } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { getCampaignDetailPath, getCampaignsPath } from "../utils/navigation";

import ModernPageLayout from "@/components/layout/ModernPageLayout";
import { useAddCampaignMutation } from "@/features/campaigns/api/useAddCampaignMutation";
import AddCampaignForm from "@/features/campaigns/components/CampaignForm/AddCampaignForm";
import { CampaignFormContentData } from "@/features/campaigns/components/CampaignForm/CampaignForm";
import { handleProblemDetailsError } from "@/lib/helpers/handleProblemDetailsError";
import { useBreadcrumbs } from "@/lib/hooks/useBreadcrumbs";
import useToast from "@/lib/hooks/useToast";
import { AddCampaignRequest } from "@/types/request/AddCampaignRequest";
import { ValidationErrorResponse } from "@/types/response/ValidationResponse";

type AddCampaignPageProps = {
  clientId?: string;
};

function AddCampaignPageContent({ clientId }: AddCampaignPageProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();

  const campaignsPath = getCampaignsPath(clientId);

  const handleNavigateBack = useCallback(() => {
    navigate(campaignsPath);
  }, [navigate, campaignsPath]);

  const addCampaign = useAddCampaignMutation(clientId);

  const handleSubmit = useCallback(
    (
      payload: AddCampaignRequest,
      setError?: UseFormSetError<CampaignFormContentData>
    ) => {
      addCampaign.mutate(payload, {
        onSuccess: ({ id }) => {
          showToast(t("admin.campaigns.campaignCreated.success"), "success");
          if (id) {
            navigate(getCampaignDetailPath(clientId, id));
          } else {
            handleNavigateBack();
          }
        },
        onError: (
          error: AxiosError<
            | ValidationErrorResponse
            | {
                title?: string;
                detail?: string;
                status?: number;
                field?: string;
              }
          >
        ) => {
          if (!error.response?.data) {
            showToast(t("errors.somethingWentWrong"), "error");
            return;
          }

          const responseData = error.response.data;

          // Handle array format (ValidationErrorResponse from FluentValidation)
          if (Array.isArray(responseData)) {
            responseData.forEach(({ propertyName, errorMessage }) => {
              if (setError) {
                setError(propertyName as keyof CampaignFormContentData, {
                  type: "manual",
                  message: t(`errorCodes.${errorMessage}`, {
                    fieldName: t(`common.${propertyName}`),
                  }),
                });
              }
            });
            return;
          }

          // Handle ProblemDetails format (RFC 7807) from handlers
          handleProblemDetailsError({
            error: error as AxiosError<{
              title: string;
              detail: string;
              status?: number;
              field?: string;
            }>,
            t,
            setError,
            showToast,
          });
        },
      });
    },
    [addCampaign, handleNavigateBack, showToast, t, navigate, clientId]
  );

  const breadcrumbs = useBreadcrumbs({
    items: [
      { label: t("common.campaigns"), href: campaignsPath },
      { label: t("common.add") },
    ],
  });

  const content: ReactNode = (
    <AddCampaignForm
      clientId={clientId}
      isSubmitting={addCampaign.isPending}
      onSubmitForm={handleSubmit}
      onSuccess={handleNavigateBack}
    />
  );

  return (
    <ModernPageLayout
      breadcrumbs={breadcrumbs}
      backbutton={{
        href: "../",
        label: t("common.back"),
      }}
      title={t("admin.campaigns.add.title")}
      subtitle={t("admin.campaigns.add.subtitle")}
    >
      {content}
    </ModernPageLayout>
  );
}

function AddCampaignPage() {
  const { clientId } = useParams();
  return <AddCampaignPageContent clientId={clientId} />;
}

export default AddCampaignPage;
